import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * DOM要素の左上の位置を取得するカスタムフック
 * @template T - HTML要素の型
 * @returns {{
 *   refCallback: (node: T | null) => void,
 *   topLeft: { top: number; left: number } | null,
 *   innerRef: React.RefObject<T | null>
 * }}
 */
function useTopLeft<T extends HTMLElement>() {
  const innerRef = useRef<T | null>(null);
  const [topLeft, setTopLeft] = useState<{ top: number; left: number } | null>(
    null,
  );

  const refCallback = useCallback((node: T | null) => {
    if (node) {
      const rect = node.getBoundingClientRect();
      setTopLeft({ top: rect.top, left: rect.left });
      innerRef.current = node;
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (innerRef.current) {
        const rect = innerRef.current.getBoundingClientRect();
        setTopLeft({ top: rect.top, left: rect.left });
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  return { refCallback, topLeft, innerRef };
}

export default useTopLeft;
